import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom'; 
import moment from 'moment-timezone';
import { getAllAssessmentToClassroomInUser } from '../../../services/userService';
import { encodeData } from '../../../utils/encodeAndEncode';
import Loader from '../../../components/common/Loader';

function Assessments({ classroom_id, classroomName }) {
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [assessments, setAssessments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const userTimeZone = moment.tz.guess(); // Get user's local timezone

  const formatDate = (utcDateString) => {
    if (!utcDateString) return '';
    
    // Convert UTC to user's local timezone
    const utcDate = moment.utc(utcDateString);
    const localDate = utcDate.tz(userTimeZone);
    
    return `${localDate.format('MMMM D, YYYY h:mm A')} (${moment.tz(userTimeZone).zoneAbbr()})`;
  };

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        setIsLoading(true);
        const payload = {
          class_id: classroom_id,
          user_id: userId,
          page: page,
          limit: itemsPerPage,
          search: search
        };

        const response = await getAllAssessmentToClassroomInUser(payload);
        // // console.log("Assessments Response:------------------------------------------", response);
        if (response.success) {
          setAssessments(response.assessments);
          setTotalPages(response.pagination?.totalPages || 1);
          setTotalRecords(response.pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.error("Error fetching assessments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (classroom_id && userId) {
      fetchAssessments();
    }
  }, [classroom_id, userId, page, itemsPerPage, search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  const isAssessmentEnabled = (assessment) => {
    if (assessment.is_time_based === 0) {
      return assessment.is_active === 1;
    } else {
      const currentTime = moment();
      const activateTime = moment.utc(assessment.activate_time).tz(userTimeZone);
      const deactivateTime = moment.utc(assessment.deactivate_time).tz(userTimeZone);
      return assessment.is_active === 1 && currentTime.isBetween(activateTime, deactivateTime, null, '[]');
    }
  };

  const handleStartAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setShowModal(true);
  };

  const handleStartQuiz = () => {
    const encodedAssessmentId = encodeData(selectedAssessment.assessment_id);
    const encodedClassroomId = encodeData(classroom_id);
    const sessionName = classroomName || selectedAssessment?.classroom_name || selectedAssessment?.session_name;
    const sessionQuery = sessionName ? `&session=${encodeURIComponent(sessionName)}` : '';
    navigate(`/user/assessmentQuiz/${encodeURIComponent(encodedAssessmentId)}?classroomid=${encodeURIComponent(encodedClassroomId)}${sessionQuery}`);
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAssessment(null);
  };
    
  return (
    <div className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
            placeholder="Search assessments..."
            aria-label="Search assessments"
            value={search}
            onChange={handleSearch}
          />
        </div>
        {search && (
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
            onClick={clearFilters}
            title="Clear search"
          >
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          {totalRecords} assessment{totalRecords !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[320px]">
            <Loader />
          </div>
        ) : (
          <table className="w-full text-sm" style={{ minWidth: '900px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assessment</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Duration</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Pass</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Best</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Attempts</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Availability</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Status</th>
                <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assessments.length > 0 ? assessments.map((assessment, index) => {
                const enabled = isAssessmentEnabled(assessment);
                const availability = assessment.is_time_based === 0
                  ? 'Always available'
                  : `${moment.utc(assessment.activate_time).local().format('DD MMM, hh:mm A')} – ${moment.utc(assessment.deactivate_time).local().format('DD MMM, hh:mm A')}`;
                return (
                  <tr key={assessment.assessment_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 align-middle text-gray-600">{((page - 1) * itemsPerPage) + index + 1}</td>
                    <td className="px-4 py-3 align-middle">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate m-0" title={assessment.assessment_name}>{assessment.assessment_name}</p>
                        {classroomName && <p className="text-xs text-gray-500 truncate m-0">{classroomName}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle text-gray-600">{assessment.duration} mins</td>
                    <td className="px-4 py-3 align-middle text-gray-600">{assessment.pass_percentage}%</td>
                    <td className="px-4 py-3 align-middle text-gray-600">{assessment.highest_percentage}%</td>
                    <td className="px-4 py-3 align-middle text-gray-600">{assessment.no_of_times_attended}</td>
                    <td className="px-4 py-3 align-middle text-gray-600">{availability}</td>
                    <td className="px-4 py-3 align-middle">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${enabled ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                        {enabled ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-[#7083FF] text-[#7083FF] hover:bg-[#7083FF]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleStartAssessment(assessment)}
                          disabled={!enabled}
                          title={enabled ? 'Start assessment' : 'Assessment not available'}
                        >
                          <Icon icon="mdi:play" width="18" height="18" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 py-16 px-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mb-4">
                        <Icon icon="mdi:clipboard-text-outline" width="36" height="36" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No assessments</h3>
                      <p className="text-gray-500 text-sm max-w-sm mx-auto">{search ? 'No assessments match your search.' : 'Assessments will appear here when your instructor adds them.'}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 px-0 pb-0">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="hidden sm:inline">Rows per page:</span>
            <select
              className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 text-sm"
              value={itemsPerPage}
              onChange={handleLimitChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-gray-500">
              {totalRecords === 0 ? 'No records' : `Showing ${(page - 1) * itemsPerPage + 1}–${Math.min(page * itemsPerPage, totalRecords)} of ${totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || totalPages === 0}
              title="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="px-2 text-gray-700">
              Page <span className="font-medium">{totalPages === 0 ? 0 : page}</span> of <span className="font-medium">{totalPages || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages || totalPages === 0}
              title="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      )}

      {/* Assessment Info Modal */}
      {showModal && selectedAssessment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="assessment-modal-title">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 id="assessment-modal-title" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-10 h-10 rounded-xl bg-[#7083FF]/10 flex items-center justify-center">
                  <Icon icon="mdi:clipboard-text" className="text-[#7083FF]" width="22" height="22" />
                </span>
                Assessment details
              </h2>
              <button
                type="button"
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <Icon icon="mdi:close" width="24" height="24" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <h3 className="text-base font-semibold text-[#7083FF]">{selectedAssessment.assessment_name}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Icon icon="mdi:clock-outline" className="text-[#7083FF] shrink-0" width="18" height="18" />
                  <span>Duration: {selectedAssessment.duration > 0 ? `${selectedAssessment.duration} min` : 'Untimed'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Icon icon="mdi:help-circle" className="text-[#7083FF] shrink-0" width="18" height="18" />
                  <span>Questions: {selectedAssessment.total_questions}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Icon icon="mdi:target" className="text-[#7083FF] shrink-0" width="18" height="18" />
                  <span>Pass: {selectedAssessment.pass_percentage}%</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Icon icon="mdi:chart-line" className="text-emerald-500 shrink-0" width="18" height="18" />
                  <span>Your best: {selectedAssessment.highest_percentage}%</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Icon icon="mdi:repeat" className="text-amber-500 shrink-0" width="18" height="18" />
                  <span>Attempts: {selectedAssessment.no_of_times_attended}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon icon={selectedAssessment.is_active ? "mdi:check-circle" : "mdi:close-circle"} className={selectedAssessment.is_active ? 'text-emerald-500' : 'text-red-500'} width="18" height="18" />
                  <span className={selectedAssessment.is_active ? 'text-emerald-600' : 'text-red-600'}>{selectedAssessment.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              {selectedAssessment.is_time_based === 1 && (
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 flex items-start gap-2">
                  <Icon icon="mdi:information" className="text-blue-500 shrink-0 mt-0.5" width="18" height="18" />
                  <div className="text-sm text-blue-800">
                    <strong>Time-bound</strong>
                    <p className="mt-1 text-blue-700">From {new Date(selectedAssessment.activate_time).toLocaleString()} until {new Date(selectedAssessment.deactivate_time).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {selectedAssessment.is_time_based === 0 && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 flex items-center gap-2 text-sm text-emerald-800">
                  <Icon icon="mdi:infinity" width="20" height="20" />
                  <strong>No time restrictions — available anytime</strong>
                </div>
              )}
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                <p className="font-semibold text-amber-800 text-sm flex items-center gap-2 mb-2">
                  <Icon icon="mdi:alert" width="18" height="18" />
                  Instructions
                </p>
                <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                  <li>Stable internet connection required</li>
                  <li>Complete in one sitting</li>
                  {selectedAssessment.duration > 0 && <li>Time limit: {selectedAssessment.duration} minutes</li>}
                  <li>You can move between questions</li>
                  <li>{selectedAssessment.duration > 0 ? 'Answers auto-submit when time expires' : 'Answers save automatically'}</li>
                </ul>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/30">
              <button type="button" className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-[#7083FF] hover:bg-[#5a6ee6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                onClick={handleStartQuiz}
                disabled={!isAssessmentEnabled(selectedAssessment)}
              >
                <Icon icon="mdi:play" width="18" height="18" />
                Start quiz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assessments;
